import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "../assets/Provinyx-logo.png";
import DemoModal from "./Demomodal";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const [demoOpen,   setDemoOpen]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const navItems = [
    { name: "Home",          path: "/" },
    { name: "About",         path: "/about" },
    // { name: "Features",      id: "features" },
    // { name: "How It Works",  id: "how-it-works" },
    // { name: "Benefits",      id: "benefits" },
    { name: "Battery Passport", path: "/battery-passport" }, 
    { name: "Authenticator", path: "/authenticator" },
    { name: "Blogs",         path: "/blog" },
    { name: "Contact",       id: "contact" },
  ];

  const handleScroll = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePathNav = (path) => {
    setMobileOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Get Started → navigates to the pricing / onboarding entry page ──
  const handleGetStarted = () => {
    setMobileOpen(false);
    navigate("/get-started");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRequestDemo = () => {
    setMobileOpen(false);
    setDemoOpen(true);
  };

  // The onboarding flow has its own dedicated header (logo + step
  // progress) — the main site navbar would be redundant/conflicting,
  // so it renders nothing at all on that route.
  if (location.pathname.startsWith("/onboarding")) {
    return null;
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-gray-200 ${
          scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">

            {/* Logo — full reload */}
            <div className="flex-shrink-0">
              <button onClick={() => { window.location.href = "/"; }} className="flex items-center focus:outline-none">
                <img src={logo} alt="Provinyx" className="h-8 sm:h-8 w-auto object-contain" />
              </button>
            </div>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
              {navItems.map((item, i) =>
                item.path ? (
                  <button
                    key={i}
                    onClick={() => handlePathNav(item.path)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      location.pathname === item.path ? "text-blue-600" : "text-gray-700 hover:text-blue-600"
                    }`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <button
                    key={i}
                    onClick={() => handleScroll(item.id)}
                    className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap"
                  >
                    {item.name}
                  </button>
                )
              )}
            </div>

            {/* Desktop CTAs — Request a Demo (left) + Get Started (right) */}
            <div className="hidden lg:flex items-center space-x-3">
              <button
                onClick={handleRequestDemo}
                className="border-2 border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-600 text-sm font-semibold px-4 xl:px-6 py-2 rounded-lg transition-colors whitespace-nowrap"
              >
                Request a Demo
              </button>
              <button
                onClick={handleGetStarted}
                className="bg-blue-600 text-white text-sm font-semibold px-4 xl:px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Get Started
              </button>
            </div>

            {/* Hamburger */}
            <button
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {navItems.map((item, i) =>
                  item.path ? (
                    <button
                      key={i}
                      onClick={() => handlePathNav(item.path)}
                      className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        location.pathname === item.path
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                      }`}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <button
                      key={i}
                      onClick={() => handleScroll(item.id)}
                      className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
                    >
                      {item.name}
                    </button>
                  )
                )}
              </div>

              <div className="mt-3 pt-4 border-t border-gray-100 flex flex-row gap-3 justify-start">
                <button
                  onClick={handleRequestDemo}
                  className="border-2 border-gray-300 text-gray-700 text-sm font-semibold px-6 py-2.5 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors"
                >
                  Request a Demo
                </button>
                <button
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto sm:px-8 bg-blue-600 text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
    </>
  );
};

export default Navbar;